import { Component, Input, OnInit } from '@angular/core';
import { Organisation } from 'src/app/models/organisation';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { IdentityUserService } from 'src/app/services/identityUser.service';
import { User } from 'src/app/models/user';
import { FormControl, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ToastService } from 'src/app/services/toast.service';
import { TranslateService } from '@ngx-translate/core';
import { NgbActiveModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { IdentityRightsService } from 'src/app/services/identityRights.service';
import { Rights } from 'src/app/models/rights';
import { lastValueFrom } from 'rxjs';
import { LoggingService } from 'src/app/services/logging.service';
import { MarkFormGroupTouchedAndDirty } from 'src/app/directives/mark-form-group-as-touched-dirty.function';
import { OnlyLettersAndNumbersValidator } from 'src/app/directives/only-letters-and-numbers-validator.directive';
import { VerifyAsEmailValidator } from 'src/app/directives/verify-as-email-validator.directive';
import { CannotContainOnlySpaceValidator } from 'src/app/directives/cannot-contain-only-space-validator.directive';
import { OrganisationSettingsApiClient } from 'src/app/services/organisation-settings.apiclient';


export const addUserModalOptions: NgbModalOptions = {
  keyboard: false,
  centered: true,
  size: 'md',
  windowClass: 'dialog-bg-white'
};


@Component({
  templateUrl: './add-user.modal.html'
})
export class AddUserModal implements OnInit {

  private _organisation!: Organisation;
  get organisation(): Organisation {
    return this._organisation;
  }
  @Input('Organisation') set organisation(val: Organisation) {
    this._organisation = val
  }
  private userGuid!: string;
  organisationGuid!: string;
  organisationName!: string;
  user: User = new User;
  translations: any = <any>{};
  userinfo!: User;
  buttonDisabled: boolean = false;

  form: UntypedFormGroup;
  firstNameControl: FormControl;
  prefixControl: FormControl;
  lastNameControl: FormControl;
  emailControl: FormControl;
  languageControl: FormControl;
  phoneControl: FormControl;
  userLanguage: string = 'Language.Dutch';
  constructor(private activeModal: NgbActiveModal, private formBuilder: UntypedFormBuilder, private authenticationService: AuthenticationService,
    private userService: IdentityUserService, private rightsService: IdentityRightsService, private toastService: ToastService, private translateService: TranslateService,
    private loggingService: LoggingService, private organisationService: OrganisationSettingsApiClient) {
    this.authenticationService.currentUser.subscribe((data) => {
      this.userGuid = data.sub;
      this.userinfo = data;
    });

    this.getTranslations();
    translateService.onLangChange.subscribe(() => {
      this.getTranslations();
    });
    this.firstNameControl = new FormControl('', [Validators.required, OnlyLettersAndNumbersValidator(), CannotContainOnlySpaceValidator()]);
    this.prefixControl = new FormControl('', [OnlyLettersAndNumbersValidator()]);
    this.lastNameControl = new FormControl('', [Validators.required, OnlyLettersAndNumbersValidator(), CannotContainOnlySpaceValidator()]);
    this.emailControl = new FormControl('', [Validators.required, VerifyAsEmailValidator()]);
    this.phoneControl = new FormControl('', [Validators.required, CannotContainOnlySpaceValidator(), Validators.maxLength(15)]);
    this.languageControl = new FormControl('', [Validators.required]);
    this.form = this.formBuilder.group(
      {
        firstname: this.firstNameControl,
        prefix: this.prefixControl,
        lastname: this.lastNameControl,
        email: this.emailControl,
        phoneNumber: this.phoneControl,
        emailLanguage: this.languageControl,
        SSOonly: new UntypedFormControl(false),
        NoAdminRights: new UntypedFormControl(true),
        OrganisationAdmin: new UntypedFormControl(false),
        EnterpriseAdmin: new UntypedFormControl(false),
        WhitelabelAdmin: new UntypedFormControl(false)
      });
  }

  getTranslations() {
    this.translateService.get([
      'Toasts.AddUserError',
      'Toasts.DeletedUserAlreadyExists',
      'Toasts.UserAlreadyExists',
      'Toasts.InvalidFormFields',
      'Toasts.Error',
      'Toasts.Saved',
      'Toasts.AddUserSuccess'
    ]).subscribe(translation => {
      this.translations.toastsAddUserError = translation['Toasts.AddUserError'];
      this.translations.toastsDeletedUserAlreadyExists = translation['Toasts.DeletedUserAlreadyExists'];
      this.translations.toastsUserAlreadyExists = translation['Toasts.UserAlreadyExists'];
      this.translations.toastsInvalidFormFields = translation['Toasts.InvalidFormFields'];
      this.translations.error = translation['Toasts.Error'];
      this.translations.toastSaved = translation['Toasts.Saved'];
      this.translations.toastsAddUserSuccess = translation['Toasts.AddUserSuccess'];

    });
  }

  async ngOnInit(): Promise<void> {
    this.getEmailLanguage();
  }

  close() {
    this.activeModal.close(false);
  }

  async SubmitUser() {
    this.buttonDisabled = true;
    if (this.form.invalid) {
      MarkFormGroupTouchedAndDirty(this.form);
      this.toastService.showInvalidFields();
      this.buttonDisabled = false;
      return;
    }
    this.user = this.form.value;
    this.user.phoneNumber = this.form.controls['phoneNumber'].value;
    this.user.organisation = this.organisationGuid;
    this.user.language = this.form.controls['emailLanguage'].value;

    let rights: Rights = new Rights();
    rights.NoAdminRights = this.form.controls['NoAdminRights'].value;
    rights.OrganisationAdmin = this.form.controls['OrganisationAdmin'].value;
    rights.WhitelabelAdmin = this.form.controls['EnterpriseAdmin'].value;
    rights.SystemAdmin = this.form.controls['WhitelabelAdmin'].value;
    this.AddUser(this.user, rights);
  }

  AddUser(user: User, rights: Rights) {
    this.userService.addUser(user)
      .then(data => {
        lastValueFrom(data)
          .then((userId: string) => {
            this.user.sub = userId;
            this.UpdateRights(userId, rights);
          })
          .catch((error) => {
            if (error.error === "Deleted user already exists") {
              this.toastService.showError("" + this.translations.error + "", "" + this.translations.toastsAddUserError + " " + this.translations.toastsDeletedUserAlreadyExists);
            } else if (error.error === "User already exists") {
              this.toastService.showError("" + this.translations.error + "", "" + this.translations.toastsAddUserError + " " + this.translations.toastsUserAlreadyExists);
            } else {
              this.toastService.showError("" + this.translations.error + "", "" + this.translations.toastsAddUserError + "");
            }
            this.buttonDisabled = false;
          });
      })
      .catch((error) => this.loggingService.logException(error));
  }

  UpdateRights(userId: string, rights: Rights) {
    this.rightsService.updateRights(userId, rights)
      .then(data => {
        lastValueFrom(data)
          .then((value: any) => {
            this.activeModal.close(false);
            this.buttonDisabled = false;
            this.toastService.showSuccess(this.translations.toastSaved, this.translations.toastsAddUserSuccess);
          })
          .catch((error) => this.loggingService.logException(error))
      })
      .catch(() => {
        this.toastService.showError("" + this.translations.error, "" + this.translations.toastsAddUserError + "");
        this.buttonDisabled = false;
      });
  }

  setAdminRights() {
    this.form.controls["NoAdminRights"].setValue(false);
  }

  setNoAdminRights() {
    this.form.controls["OrganisationAdmin"].setValue(false);
    this.form.controls["EnterpriseAdmin"].setValue(false);
    this.form.controls["WhitelabelAdmin"].setValue(false);
  }

  getEmailLanguage() {
    this.organisationService.getOrganisationSettings(this.organisationGuid).subscribe((settings) => {
      let organisationLanguageIndex: number = settings.findIndex(setting => setting.key === "OrganisationLanguage");
      if (organisationLanguageIndex > -1) {
        this.setUserLanguage(settings[organisationLanguageIndex].value);
      }
    });
  }


  setUserLanguageWithEvent(event: Event, language: string) {
    event.preventDefault();
    this.setUserLanguage(language);
  }

  private setUserLanguage(language: string) {
    if (language && language.toLowerCase() === 'en') {
      this.user.language = 'en';
      this.languageControl.setValue('en');
      this.userLanguage = 'Language.English';
    } else {
      this.user.language = 'nl';
      this.languageControl.setValue('nl');
      this.userLanguage = 'Language.Dutch';
    }
  }
}
